import React, { useState, useContext, useEffect, useRef } from 'react';
import NewForm from './NewForm';
import FormGridItem from './FormGridItem';
import { UserContext } from '../../hooks/Contexts';
import { copyForm } from './constants/handleForm';
import FormMoverModal from './FormMoveModal';
import { confirmation, loadingBlock } from '../../helpers/notifications';
import Icon from '../common/Icon';
import { sortByAlpha } from '../../helpers/sorters';
import QuickFilter from '../common/QuickFilter';

const FormIndex = ({ formsProp, container }) => {
  const [forms, setForms] = useState(formsProp);
  const formsRef = useRef();
  const [viewMoverModal, setViewMoverModal] = useState(false);
  const [showArchived, setShowArchived] = useState(container.archived);
  const [showInactive, setShowInactive] = useState(false);
  const [formToMove, setFormToMove] = useState(null);
  const [filter, setFilter] = useState('');
  const user = useContext(UserContext);
  const nonArchivedForms = forms.filter((f) => !f.archived);
  const archivedForms = forms.filter((f) => f.archived);
  const transitionStyle = {
    transition: 'max-height 0.5s ease-in-out',
    overflowY: 'clip',
  };

  useEffect(() => {
    determineHeight('archived-forms', showArchived);
    determineHeight('inactive-forms', showInactive);
  }, [showArchived, showInactive, forms]);

  useEffect(() => {
    if (!formsRef.current) formsRef.current = formsProp;
    let showForms = formsRef.current;
    if (filter) {
      showForms = showForms.filter((f) => {
        let values = [f.name?.toLowerCase(), f.display_name?.toLowerCase()];
        return values.some((names) => names.includes(filter.toLowerCase()));
      });
      setForms(sortByAlpha(showForms, 'name'));
      if (!showInactive) setShowInactive(true);
      if (!showArchived) setShowArchived(true);
    } else {
      if (showInactive) setShowInactive(false);
      if (showArchived) setShowArchived(false);
      setForms(sortByAlpha(showForms, 'name'));
    }
  }, [filter]);

  const handleFilterChange = (e) => {
    e.preventDefault();
    setFilter(e.target.value);
  };

  const determineHeight = (elementId, showBool) => {
    let element = document.getElementById(elementId);
    if (element) {
      let height = element.scrollHeight;
      element.style.maxHeight = showBool ? `${height}px` : '0px';
    }
  };

  const updateFormIndex = (data) => {
    setForms(sortByAlpha(forms.concat(data), 'name'));
  };

  const handleFormMove = (form) => {
    setFormToMove(form);
    setViewMoverModal(true);
  };

  const handleModalClose = () => {
    setFormToMove(null);
    setViewMoverModal(false);
  };

  const handleFormCopy = async (form) => {
    confirmation.copy(async () => {
      loadingBlock.add('.grid', 'Copying form...');
      let result = await copyForm(form.id);
      if (result.ok) {
        setForms(sortByAlpha([...forms, result.data], 'name'));
      }
      loadingBlock.remove('.grid');
    });
  };

  return (
    <section className='forms'>
      <h2>Forms</h2>
      {user.isAdmin && <NewForm updateFormIndex={updateFormIndex} />}
      <QuickFilter
        filter={filter}
        clearFilter={() => setFilter('')}
        handleFilterChange={handleFilterChange}
        placeholder={'Form name...'}
      />

      <div className='grid-container'>
        {nonArchivedForms.filter((f) => f.active).length > 0 ? (
          <div className='grid'>
            {nonArchivedForms
              .filter((f) => f.active)
              .map((form) => {
                return (
                  <FormGridItem
                    form={{ ...form, container_slug: container.slug }}
                    key={`form-id-${form.id}`}
                    handleFormCopy={handleFormCopy}
                    handleFormMove={handleFormMove}
                    shown={true}
                  />
                );
              })}
          </div>
        ) : (
          <p className='none'>No active forms to show</p>
        )}
        <hr />
      </div>

      <h3 onClick={() => setShowInactive(!showInactive)} className='clickable'>
        Inactive Forms{' '}
        <Icon
          title={showInactive ? 'Hide' : 'Show'}
          icon={`angle-${showInactive ? 'up' : 'down'} fa-border`}
        />
      </h3>
      <div
        id='inactive-forms'
        className='grid-container'
        style={transitionStyle}
      >
        <div className='grid' style={{ paddingTop: '0.5rem' }}>
          {nonArchivedForms.filter((f) => !f.active).length > 0 ? (
            nonArchivedForms
              .filter((f) => !f.active)
              .map((form) => {
                return (
                  <FormGridItem
                    form={{ ...form, container_slug: container.slug }}
                    key={`form-id-${form.id}`}
                    handleFormCopy={handleFormCopy}
                    handleFormMove={handleFormMove}
                    shown={showInactive}
                  />
                );
              })
          ) : (
            <p className='none'>No inactive forms to show</p>
          )}
        </div>
      </div>
      <hr />

      {/* Archived Forms -- show only if any exist */}
      {archivedForms.length > 0 && (
        <>
          <h3
            onClick={() => setShowArchived(!showArchived)}
            className='clickable'
          >
            Archived Forms{' '}
            <Icon
              title={showArchived ? 'Hide' : 'Show'}
              icon={`angle-${showArchived ? 'up' : 'down'} fa-border`}
            />
          </h3>
          <div id='archived-forms' style={transitionStyle}>
            <div className='grid' style={{ paddingTop: '0.5rem' }}>
              {archivedForms.map((form) => {
                return (
                  <FormGridItem
                    form={{ ...form, container_slug: container.slug }}
                    key={`form-id-${form.id}`}
                    handleFormCopy={handleFormCopy}
                    handleFormMove={handleFormMove}
                    shown={showArchived}
                  />
                );
              })}
            </div>
          </div>
          <hr />
        </>
      )}

      {viewMoverModal && (
        <FormMoverModal
          viewMoverModal={viewMoverModal}
          closeMoverModal={handleModalClose}
          form={formToMove}
          setForms={setForms}
        />
      )}
    </section>
  );
};

export default FormIndex;
